/**
 * Observe Shopify Forms field id="custom#ico", validate via ARES,
 * and prefill company / shipping address fields.
 *
 * Hook: document.addEventListener('yesme:custom-ico', (e) => { ... e.detail })
 */
(function () {
	const FIELD_ID = 'custom#ico';
	const HOST_SELECTOR = 'shopify-forms-embed, form-embed';
	const ARES_URL = 'https://ares.gov.cz/ekonomicke-subjekty-v-be/rest/ekonomicke-subjekty/';
	const STATUS_ID = 'yesme-ico-status';
	const DEBUG = true;
	const MAX_POLL_MS = 30000;
	const POLL_MS = 250;

	const MESSAGES = {
		validating: 'Ověřujeme IČO…',
		success: 'Firma nalezena, údaje doplněny.',
		notFound: 'IČO nebylo v ARES nalezeno.',
		invalid: 'Zadejte platné 8místné IČO.',
		error: 'Ověření IČO se nezdařilo. Zkuste to znovu.',
	};

	const PREFILL_IDS = [
		'company_name',
		'company_tax_id',
		'shipping_address_recipient',
		'shipping_address_address1',
		'shipping_address_address2',
		'shipping_address_zip',
		'shipping_address_city',
	];

	let bound = false;
	let shadowObserver = null;
	let hostRef = null;
	let fieldRef = null;
	let lastRequestedIco = '';
	let requestToken = 0;

	function log() {
		if (!DEBUG) return;
		console.log('[yesme:custom-ico]', ...arguments);
	}

	function normalizeIco(value) {
		const digits = String(value || '').replace(/\D/g, '');
		if (!digits) return '';
		if (digits.length > 8) return digits.slice(0, 8);
		return digits.padStart(8, '0');
	}

	function isCompleteIco(value) {
		return /^\d{8}$/.test(String(value || '').replace(/\D/g, '').padStart(8, '0')) &&
			String(value || '').replace(/\D/g, '').length >= 8;
	}

	function rawDigits(value) {
		return String(value || '').replace(/\D/g, '');
	}

	function getShadowRoot() {
		return hostRef?.shadowRoot || null;
	}

	function getField(host) {
		const root = host?.shadowRoot;
		if (!root) return null;
		return root.getElementById(FIELD_ID) || root.querySelector('[id="custom#ico"]');
	}

	function getFieldContainer(field) {
		return field.closest('._formFieldContainer_1mxsl_5, [class*="_formFieldContainer_"]') || field.parentElement;
	}

	function ensureStatusEl(field) {
		const root = getShadowRoot();
		if (!root || !field) return null;

		let status = root.getElementById(STATUS_ID);
		if (status) return status;

		status = document.createElement('p');
		status.id = STATUS_ID;
		status.setAttribute('role', 'status');
		status.setAttribute('aria-live', 'polite');
		status.style.cssText = [
			'margin: 0.4rem 0 0',
			'font-size: 1.2rem',
			'line-height: 1.4',
			'text-align: left',
			'color: #5c5f62',
		].join(';');

		const container = getFieldContainer(field);
		if (container?.parentNode) {
			container.insertAdjacentElement('afterend', status);
		} else {
			field.insertAdjacentElement('afterend', status);
		}

		return status;
	}

	function setStatus(type, text) {
		if (!fieldRef) return;
		const status = ensureStatusEl(fieldRef);
		if (!status) return;

		status.textContent = text || '';
		status.dataset.state = type || '';

		const colors = {
			validating: '#5c5f62',
			success: '#1878b9',
			notFound: '#e02229',
			invalid: '#e02229',
			error: '#e02229',
			idle: 'transparent',
		};

		status.style.color = colors[type] || colors.validating;
		status.style.display = text ? 'block' : 'none';
	}

	function setNativeValue(field, value) {
		const proto = field instanceof HTMLTextAreaElement
			? window.HTMLTextAreaElement.prototype
			: window.HTMLInputElement.prototype;
		const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
		if (setter) setter.call(field, value);
		else field.value = value;

		field.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
		field.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
	}

	function fillField(id, value) {
		const root = getShadowRoot();
		if (!root || value == null || value === '') return false;
		const field = root.getElementById(id) || root.querySelector(`[id="${id}"]`);
		if (!field) {
			log('prefill target missing', id);
			return false;
		}
		setNativeValue(field, String(value));
		return true;
	}

	function buildStreet(sidlo) {
		if (!sidlo) return '';
		const street = sidlo.nazevUlice || '';
		const house = sidlo.cisloDomovni != null ? String(sidlo.cisloDomovni) : '';
		const orient = sidlo.cisloOrientacni != null ? String(sidlo.cisloOrientacni) : '';
		const letter = sidlo.cisloOrientacniPismeno || '';
		let number = house;
		if (orient) number = house ? `${house}/${orient}${letter}` : `${orient}${letter}`;
		else if (letter && house) number = `${house}${letter}`;
		if (street && number) return `${street} ${number}`;
		return street || number || sidlo.adresaDorucovaci?.radekAdresy1 || '';
	}

	function buildCity(sidlo) {
		if (!sidlo) return '';
		if (sidlo.nazevObce === 'Praha' && sidlo.nazevMestskeCastiObvodu) {
			return sidlo.nazevMestskeCastiObvodu;
		}
		return sidlo.nazevObce || '';
	}

	function buildZip(sidlo) {
		if (!sidlo?.psc) return '';
		return String(sidlo.psc).padStart(5, '0');
	}

	function mapAresToForm(data) {
		const sidlo = data.sidlo || {};
		return {
			company_name: data.obchodniJmeno || '',
			company_tax_id: data.dic || '',
			shipping_address_recipient: data.obchodniJmeno || '',
			shipping_address_address1: buildStreet(sidlo),
			shipping_address_address2: '',
			shipping_address_zip: buildZip(sidlo),
			shipping_address_city: buildCity(sidlo),
		};
	}

	function prefillFromAres(data) {
		const mapped = mapAresToForm(data);
		PREFILL_IDS.forEach((id) => fillField(id, mapped[id]));
		log('prefilled', mapped);
		return mapped;
	}

	async function lookupIco(ico) {
		const token = ++requestToken;
		lastRequestedIco = ico;
		setStatus('validating', MESSAGES.validating);
		log('lookup', ico);

		try {
			const response = await fetch(`${ARES_URL}${encodeURIComponent(ico)}`, {
				headers: { Accept: 'application/json' },
			});

			if (token !== requestToken) return;

			if (response.status === 404) {
				setStatus('notFound', MESSAGES.notFound);
				document.dispatchEvent(new CustomEvent('yesme:custom-ico', {
					detail: { filled: true, value: ico, valid: false, reason: 'notFound' },
				}));
				return;
			}

			if (!response.ok) {
				setStatus('error', MESSAGES.error);
				document.dispatchEvent(new CustomEvent('yesme:custom-ico', {
					detail: { filled: true, value: ico, valid: false, reason: 'error', status: response.status },
				}));
				return;
			}

			const data = await response.json();
			if (token !== requestToken) return;

			const mapped = prefillFromAres(data);
			setStatus('success', MESSAGES.success);

			document.dispatchEvent(new CustomEvent('yesme:custom-ico', {
				detail: {
					filled: true,
					value: ico,
					valid: true,
					ares: data,
					mapped,
					field: fieldRef,
				},
			}));
		} catch (error) {
			if (token !== requestToken) return;
			log('lookup failed', error);
			setStatus('error', MESSAGES.error);
			document.dispatchEvent(new CustomEvent('yesme:custom-ico', {
				detail: { filled: true, value: ico, valid: false, reason: 'error', error },
			}));
		}
	}

	function handleFieldUpdate(field) {
		const digits = rawDigits(field.value);

		document.dispatchEvent(new CustomEvent('yesme:custom-ico', {
			detail: {
				filled: Boolean(digits),
				value: field.value,
				digits,
				field,
			},
		}));

		if (digits.length < 8) {
			requestToken += 1;
			lastRequestedIco = '';
			if (digits.length === 0) setStatus('idle', '');
			else setStatus('idle', '');
			return;
		}

		const ico = normalizeIco(digits.slice(0, 8));
		if (ico === lastRequestedIco) return;
		lookupIco(ico);
	}

	function bindField(field, host) {
		if (!field || field.dataset.icoObserved === 'true') return false;

		field.dataset.icoObserved = 'true';
		bound = true;
		hostRef = host;
		fieldRef = field;

		ensureStatusEl(field);

		field.addEventListener('input', () => handleFieldUpdate(field));
		field.addEventListener('change', () => handleFieldUpdate(field));
		handleFieldUpdate(field);

		log('bound to field', field);
		return true;
	}

	function watchShadow(host) {
		if (!host.shadowRoot) {
			log('host found but shadowRoot is null (closed or not ready)', host);
			return false;
		}

		if (bindField(getField(host), host)) {
			if (shadowObserver) shadowObserver.disconnect();
			return true;
		}

		if (shadowObserver) return false;

		shadowObserver = new MutationObserver(() => {
			if (bindField(getField(host), host)) {
				shadowObserver.disconnect();
				shadowObserver = null;
			}
		});

		shadowObserver.observe(host.shadowRoot, {
			childList: true,
			subtree: true,
		});

		log('watching shadowRoot for field', FIELD_ID);
		return false;
	}

	function findHosts() {
		return Array.from(document.querySelectorAll(HOST_SELECTOR));
	}

	function tryBind() {
		if (bound) return true;

		const hosts = findHosts();
		if (!hosts.length) return false;

		for (const host of hosts) {
			if (watchShadow(host)) return true;
		}

		return false;
	}

	function start() {
		log('starting observer');

		if (tryBind()) return;

		const lightObserver = new MutationObserver(() => {
			if (tryBind() && bound) lightObserver.disconnect();
		});

		lightObserver.observe(document.documentElement, {
			childList: true,
			subtree: true,
		});

		const startedAt = Date.now();
		const poll = window.setInterval(() => {
			if (tryBind() || Date.now() - startedAt > MAX_POLL_MS) {
				window.clearInterval(poll);
				if (!bound) log('gave up waiting for field', FIELD_ID);
			}
		}, POLL_MS);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', start, { once: true });
	} else {
		start();
	}
})();

/**
 * Restrict Identixweb delivery calendar to weekdays from
 * company location metafield delivery.days (Po, Út, St, Čt, Pá, …).
 * Empty/missing metafield → Sunday only (Ne), supplied by delivery-days-data.
 */
(function () {
	const DATA_ID = 'yesme-delivery-days';
	const CALENDAR_ROOT = '.identixweb-order-delivery';
	const DAY_SHORT = ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'];
	const RETRY_MS = [0, 50, 100, 200, 400, 800, 1200];
	const DEBUG = true;

	let applying = false;

	function log() {
		if (!DEBUG) return;
		console.log('[yesme:delivery-days]', ...arguments);
	}

	function normalizeDay(day) {
		return String(day || '').normalize('NFC').trim().toLocaleLowerCase('cs');
	}

	function readAllowedDays() {
		const el = document.getElementById(DATA_ID);
		if (!el) return null;

		try {
			const value = JSON.parse(el.textContent.trim() || '[]');
			if (!Array.isArray(value) || !value.length) return null;

			const allowed = new Set(
				value.map(normalizeDay).filter(Boolean)
			);

			return allowed.size ? allowed : null;
		} catch (error) {
			log('failed to parse delivery days', error);
			return null;
		}
	}

	function weekdayFromLabel(label) {
		if (!label || !/^\d{4}-\d{2}-\d{2}$/.test(label)) return null;
		const [year, month, day] = label.split('-').map(Number);
		const date = new Date(year, month - 1, day);
		if (Number.isNaN(date.getTime())) return null;
		return normalizeDay(DAY_SHORT[date.getDay()]);
	}

	function ensureBlocked(dayEl, span) {
		let changed = false;

		if (!dayEl.classList.contains('rmdp-disabled')) {
			dayEl.classList.add('rmdp-disabled');
			changed = true;
		}
		if (dayEl.dataset.yesmeBlocked !== 'true') {
			dayEl.dataset.yesmeBlocked = 'true';
			changed = true;
		}

		if (span) {
			if (span.classList.contains('sd')) {
				span.classList.remove('sd');
				changed = true;
			}
			if (span.getAttribute('tabindex') !== '-1') {
				span.setAttribute('tabindex', '-1');
				changed = true;
			}
			if (span.getAttribute('aria-disabled') !== 'true') {
				span.setAttribute('aria-disabled', 'true');
				changed = true;
			}
		}

		return changed;
	}

	function clearBlockedSelection(root) {
		const selected = root.querySelector('.rmdp-day.rmdp-selected[data-yesme-blocked="true"]');
		if (!selected) return;

		const dateInput = root.querySelector('.deliveryDateValue, .uiDeliveryDateVal');
		if (dateInput) dateInput.value = '';
		const dayInput = root.querySelector('.deliveryDayValue');
		if (dayInput) dayInput.value = '';
		selected.classList.remove('rmdp-selected');
		log('cleared selected date outside allowed weekdays');
	}

	function applyAllowedDays(root, allowed) {
		const days = root.querySelectorAll('.rmdp-day:not(.rmdp-day-hidden)');
		let blocked = 0;

		days.forEach((dayEl) => {
			const span = dayEl.querySelector('span[aria-label]');
			const weekday = weekdayFromLabel(span?.getAttribute('aria-label'));
			if (!weekday) return;

			if (!allowed.has(weekday)) {
				if (ensureBlocked(dayEl, span)) blocked += 1;
			}
		});

		clearBlockedSelection(root);
		if (blocked) log('blocked/re-blocked days', blocked);
	}

	function start() {
		const allowed = readAllowedDays();
		if (!allowed) {
			log('no delivery.days restrictions — calendar untouched');
			return;
		}

		log('allowed weekdays', [...allowed]);

		let applyTimer = null;
		const retryTimers = [];

		const applyAll = () => {
			if (applying) return;
			applying = true;
			try {
				document.querySelectorAll(CALENDAR_ROOT).forEach((root) => {
					applyAllowedDays(root, allowed);
				});
			} finally {
				applying = false;
			}
		};

		const scheduleApply = () => {
			window.clearTimeout(applyTimer);
			applyTimer = window.setTimeout(applyAll, 30);
		};

		const retryApply = () => {
			retryTimers.forEach((id) => window.clearTimeout(id));
			retryTimers.length = 0;
			RETRY_MS.forEach((ms) => {
				retryTimers.push(window.setTimeout(applyAll, ms));
			});
		};

		applyAll();

		const observer = new MutationObserver((mutations) => {
			if (applying) return;

			// Ignore our own data-yesme-blocked attribute writes when possible.
			const relevant = mutations.some((mutation) => {
				if (mutation.type === 'childList') return true;
				if (mutation.type !== 'attributes') return false;
				if (mutation.attributeName === 'data-yesme-blocked') return false;
				return true;
			});

			if (relevant) scheduleApply();
		});

		observer.observe(document.documentElement, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['class', 'aria-label', 'aria-disabled', 'tabindex'],
		});

		document.addEventListener(
			'click',
			(event) => {
				const nav = event.target?.closest?.(
					'.prev_next_button, .next_header_btn, .prev_header_btn, .rmdp-arrow-container, .rmdp-arrow'
				);
				if (nav) {
					log('month navigation — retry filter');
					retryApply();
					return;
				}

				const blocked = event.target?.closest?.(
					'.rmdp-day[data-yesme-blocked="true"], .rmdp-day[data-yesme-blocked="true"] *'
				);
				if (!blocked) return;

				event.preventDefault();
				event.stopPropagation();
			},
			true
		);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', start, { once: true });
	} else {
		start();
	}
})();
